import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { quotationService } from '@/lib/supabase/quotations';

interface QuotationSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (quotationId: string) => void;
  currentQuotationId: string;
}

interface QuotationOption {
  id: string;
  title: string;
  client?: string;
  destination: string[] | string;
  created_at?: string;
}

export function QuotationSelectionModal({
  isOpen,
  onClose,
  onSelect,
  currentQuotationId
}: QuotationSelectionModalProps) {
  const [quotations, setQuotations] = useState<QuotationOption[]>([]);
  const [filteredQuotations, setFilteredQuotations] = useState<QuotationOption[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Verificar se onSelect é uma função
  useEffect(() => {
    if (typeof onSelect === 'function') {
      // Removed console.log statement
    }
  }, [onSelect]);

  // Carregar cotações quando o modal é aberto
  useEffect(() => {
    if (isOpen) {
      loadQuotations();
    }
  }, [isOpen]);

  // Filtrar cotações quando o termo de busca muda
  useEffect(() => {
    filterQuotations();
  }, [searchTerm, quotations]);

  const loadQuotations = async () => {
    try {
      setIsLoading(true);
      const loadedQuotations = await quotationService.getQuotations();
      
      // Formatar dados e remover a cotação atual da lista
      const formattedQuotations = loadedQuotations
        .filter(q => q.id !== currentQuotationId)
        .map(q => ({
          id: q.id,
          title: q.title || 'Sem título',
          client: q.client || '',
          destination: q.destination || [],
          created_at: q.created_at,
          pageCount: q.pages?.length || 0
        }));
      
      setQuotations(formattedQuotations);
      setFilteredQuotations(formattedQuotations);
    } catch (error) {
      console.error('Erro ao carregar cotações:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar as cotações',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filterQuotations = () => {
    if (!searchTerm.trim()) {
      setFilteredQuotations(quotations);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = quotations.filter(
      q => 
        q.title.toLowerCase().includes(term) || 
        (q.client && q.client.toLowerCase().includes(term)) ||
        (Array.isArray(q.destination) 
          ? q.destination.some(d => typeof d === 'string' && d.toLowerCase().includes(term))
          : typeof q.destination === 'string' && q.destination.toLowerCase().includes(term))
    );
    
    setFilteredQuotations(filtered);
  };

  // Função para lidar com a seleção de uma cotação
  const handleSelect = useCallback((quotationId: string) => {
    // Removed console.log statements
    
    if (typeof onSelect === 'function') {
      try {
        onSelect(quotationId);
        // Removed console.log statement
        
        // Fechar o modal
        onClose();
      } catch (error) {
        console.error('Erro ao selecionar cotação:', error);
      }
    }
  }, [onSelect, onClose]);

  const formatDestination = (destination: string[] | string): string => {
    if (Array.isArray(destination)) {
      return destination.join(', ');
    }
    return destination as string;
  };

  // Evitar fechar o modal ao clicar dentro dele
  const handleDialogClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Componente para exibir uma cotação na lista
  const QuotationItem = useCallback(({ quotation }: { quotation: QuotationOption }) => {
    return (
      <div
        className="flex flex-col p-3 border rounded-md cursor-pointer hover:bg-gray-50"
        onClick={(e) => {
          // Prevenir todos os comportamentos padrão
          e.preventDefault();
          e.stopPropagation();
          
          // Chamar handleSelect com o ID da cotação
          handleSelect(quotation.id);
        }}
      >
        <div className="font-medium">{quotation.title}</div>
        <div className="text-sm text-gray-500">
          {quotation.client && (
            <span className="mr-2">Cliente: {quotation.client}</span>
          )}
          {quotation.destination && (
            <span>Destino: {formatDestination(quotation.destination)}</span>
          )}
        </div>
      </div>
    );
  }, [handleSelect, formatDestination]);

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => {
        // Só chamar onClose quando o estado mudar para fechado
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-md" onClick={handleDialogClick}>
        <DialogHeader>
          <DialogTitle>Selecionar Cotação de Destino</DialogTitle>
          <DialogDescription>
            Selecione a cotação para onde deseja copiar esta página
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <div className="relative mb-4">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              type="text"
              placeholder="Buscar cotação..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => {
                const searchQuery = e.target.value.toLowerCase();
                setSearchTerm(searchQuery);
              }}
            />
          </div>
          
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
            </div>
          ) : (
            <div className="max-h-[300px] overflow-y-auto">
              {filteredQuotations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nenhuma cotação encontrada
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredQuotations.map((quotation) => (
                    <QuotationItem key={quotation.id} quotation={quotation} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}>
            Cancelar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 